import { AppIcon, type IconDefinition } from '@/components/ui/app-icon';
import { iconSidebar, iconSidebarFlip } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

/**
 * The one control that opens or closes a side panel or drawer, anywhere in
 * the app: the tabbed sidebar's own drawer icon, 28px square with a 15px
 * glyph, the sidebar's grey and its hover fill. The icon draws the panel on
 * the edge it sits on, so a left panel (the sidebar, the Clu dock, the search
 * filters) takes `iconSidebar` and a right drawer (Actions, record details, a
 * builder's step panel) takes the mirrored `iconSidebarFlip`. Modals and
 * dialogs keep their close X.
 */

/** The edge the panel sits on. */
export type PanelSide = 'left' | 'right';

const PANEL_TOGGLE_ICON: Record<PanelSide, IconDefinition> = {
  left: iconSidebar,
  right: iconSidebarFlip,
};

const ICON_SIZE = 15;

/** Size, colour, hover and focus of every panel toggle. */
const PANEL_TOGGLE_CLASS =
  'relative inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors ' +
  'text-gray-600 hover:bg-black/[0.05] dark:text-gray-400 dark:hover:bg-white/[0.06] ' +
  'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/50';

/** A toggle floating over a canvas keeps an opaque fill under the hover. */
const FLOATING_CLASS =
  'border border-border/60 bg-background shadow-xs hover:bg-muted dark:hover:bg-muted';

/** The drawer icon. */
function PanelToggleIcon({
  side = 'left',
  className,
}: {
  side?: PanelSide;
  className?: string;
}) {
  return (
    <AppIcon
      icon={PANEL_TOGGLE_ICON[side]}
      aria-hidden
      data-panel-toggle-icon={side}
      className={className}
      style={{ fontSize: ICON_SIZE }}
    />
  );
}

type PanelToggleButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'aria-label'
> & {
  /** The accessible name, such as "Hide sidebar" or "Close". */
  label: string;
  side?: PanelSide;
  /**
   * Shown at rest in the icon's place; the drawer icon crossfades in on hover
   * and on keyboard focus. The folded sidebar's Cleanlist leaf.
   */
  resting?: ReactNode;
  /** Raised off a canvas: an opaque card with a border and a shadow. */
  floating?: boolean;
  /** Dots and badges over the icon. */
  children?: ReactNode;
};

export const PanelToggleButton = forwardRef<
  HTMLButtonElement,
  PanelToggleButtonProps
>(function PanelToggleButton(
  {
    label,
    side = 'left',
    resting,
    floating = false,
    className,
    children,
    type,
    ...rest
  },
  ref
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-label={label}
      data-panel-toggle={side}
      className={cn(
        PANEL_TOGGLE_CLASS,
        floating && FLOATING_CLASS,
        resting && 'group/panel-toggle',
        className
      )}
      {...rest}
    >
      {resting ? (
        <>
          <span
            aria-hidden
            data-panel-toggle-resting
            className='inline-flex items-center justify-center transition-opacity duration-150 group-hover/panel-toggle:opacity-0 group-focus-visible/panel-toggle:opacity-0 motion-reduce:transition-none'
          >
            {resting}
          </span>
          <PanelToggleIcon
            side={side}
            className='absolute opacity-0 transition-opacity duration-150 group-hover/panel-toggle:opacity-100 group-focus-visible/panel-toggle:opacity-100 motion-reduce:transition-none'
          />
        </>
      ) : (
        <PanelToggleIcon side={side} />
      )}
      {children}
    </button>
  );
});
